import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Portal from 'react-portal';
import AutoComplete from 'react-autocomplete';
import Highlighter from 'react-highlight-words';
import { HotKeys } from 'react-hotkeys';
import cn from 'classnames';
import TextCropper from '@omilia/text-cropper';

import { dictionaryType } from 'types';
import Spinner from 'components/Spinner';
import Columns from './components/Columns';

import './style.scss';

const keyMap = {
  leave: ['esc'],
};

export default class DictionaryPopup extends Component {
  handleHotKeys = {
    leave: e => this.handleLeave(e),
  };

  static propTypes = {
    dictionaries: PropTypes.arrayOf(dictionaryType),
    isAdding: PropTypes.bool,
    isAlignedToTop: PropTypes.bool,

    isLoading: PropTypes.bool,
    position: PropTypes.shape({
      left: PropTypes.string,
      top: PropTypes.string,
    }),
    token: PropTypes.string,
    onAddDictionaryEntry: PropTypes.func,
    onClose: PropTypes.func,
    onGetDictionaries: PropTypes.func,
  };

  state = {
    isOpen: true,
    isSelected: false,
    searchTerm: '',
    dictionary: {},
  };

  componentDidMount() {
    if (this.input) {
      this.input.focus();
    }
  }

  handleOpen = () => {
    this.setState({ isOpen: true }, () => this.input.focus());
  };

  handleClose = () => {
    this.setState({ isOpen: false, searchTerm: '' });
  };

  matchItemToTerm = (item, value) => {
    if (value === '') {
      return true;
    }
    return item.name.toLowerCase().includes(value.toLowerCase());
  };

  handleGetItemValue = option => option.name;

  handleChange = value => {
    this.setState(
      {
        searchTerm: value,
      },
      () => {
        const { onGetDictionaries } = this.props;

        onGetDictionaries(value);
      },
    );
  };

  handleSelect = dictionary => {
    this.setState({
      searchTerm: dictionary.name,
      isOpen: false,
      isSelected: true,
      dictionary,
    });
  };

  handleLeave = e => {
    e.cancelBubble = true;

    const { searchTerm } = this.state;
    const { onClose } = this.props;

    if (searchTerm !== '') {
      this.setState({ searchTerm: '' }, () => this.input.focus());
    } else {
      this.setState({ isOpen: false }, () => onClose());
    }
  };

  handleRef = ref => {
    this.input = ref;
  };

  handleSubmit = async data => {
    const { onAddDictionaryEntry, onClose } = this.props;
    const { dictionary } = this.state;

    await onAddDictionaryEntry(dictionary.id, data);
    this.setState({ isOpen: false }, () => onClose());
  };

  renderInput = props => {
    const { isLoading } = this.props;
    const { isOpen } = this.state;
    return (
      <HotKeys handlers={this.handleHotKeys} keyMap={keyMap}>
        <div styleName="search-container">
          {!isOpen && !isLoading && (
            <button type="button" onClick={this.handleOpen}>
              Choose dictionary
            </button>
          )}
          <input
            data-test="search-input"
            placeholder="Choose dictionary ..."
            styleName={cn('input', {
              'is-active': !isOpen && !isLoading,
            })}
            {...props}
          />

          {isLoading && <Spinner isBlue styleName="spinner" />}
        </div>
      </HotKeys>
    );
  };

  renderItem = (item, isHighlighted) => {
    const { searchTerm } = this.state;

    return (
      <div
        key={item.id}
        styleName={cn('item', {
          'is-active': isHighlighted,
        })}
      >
        <TextCropper placement="right" styleName="cropper" text={item.name}>
          <Highlighter
            autoEscape
            searchWords={[searchTerm]}
            textToHighlight={item.name}
          />
        </TextCropper>
      </div>
    );
  };

  renderNoItems = () => {
    const { isLoading } = this.props;

    if (isLoading) {
      return null;
    }

    return <span styleName="item no-items">No dictionaries found</span>;
  };

  render() {
    const { isSelected, dictionary, isOpen, searchTerm } = this.state;
    const {
      onClose,
      position,
      isAlignedToTop,
      dictionaries,
      token,
      isAdding,
      isLoading,
    } = this.props;

    return (
      <Portal closeOnEsc closeOnOutsideClick isOpened onClose={onClose}>
        <div
          style={position}
          styleName={cn('root', {
            columns: isSelected,
            'is-bottom-aligned': !isAlignedToTop,
          })}
        >
          {!isSelected ? (
            <AutoComplete
              ref={this.handleRef}
              autoHighlight
              getItemValue={this.handleGetItemValue}
              inputProps={{
                onBlur: this.handleClose,
              }}
              items={dictionaries}
              open={isOpen && !isLoading}
              renderInput={this.renderInput}
              renderItem={this.renderItem}
              renderMenu={children => (
                <div styleName="menu">
                  {children.length > 0 ? children : this.renderNoItems()}
                </div>
              )}
              shouldItemRender={this.matchItemToTerm}
              value={searchTerm}
              wrapperStyle={{ display: 'block' }}
              onChange={(event, value) => this.handleChange(value)}
              onSelect={(value, item) => this.handleSelect(item)}
            />
          ) : (
            <Columns
              dictionary={dictionary}
              initialValues={{
                token,
                values: {},
              }}
              isAdding={isAdding}
              token={token}
              onClose={onClose}
              onSubmit={this.handleSubmit}
            />
          )}
        </div>
      </Portal>
    );
  }
}
